<template>
  <div style="padding: 16px; background: #F8F8F9;">
    <div class="cm-panel">
      <div class="cm-panel-head">
        <div class="cm-panel-title">Assignment Rules<span class="sub" v-if="ruleCount">{{ ruleCount }} programs</span></div>
        <div class="cm-panel-head-actions" style="display: flex; align-items: center; gap: 8px;">
          <template v-if="myTeam.available || scopeMode === 'my_team'">
            <div class="cm-seg" style="margin-right: 4px;">
              <button :class="{ on: scopeMode === 'all' }" @click="scopeMode = 'all'; closeBalance()" :disabled="balancing || rebalancing">All</button>
              <button :class="{ on: scopeMode === 'my_team' }" @click="scopeMode = 'my_team'; closeBalance()" :disabled="balancing || rebalancing || !myTeamMappingReady">My Team</button>
            </div>
          </template>

          <button
            class="cm-btn cm-btn-secondary cm-btn-sm"
            @click="runRebalance"
            :disabled="rebalancing || balancing || !actionsEnabled"
          >
            <FontAwesomeIcon :icon="['fas', 'wand-magic-sparkles']" style="font-size: 11px;" />
            {{ rebalancing ? 'Assigning…' : 'Assign Unassigned' }}
          </button>
          <button
            class="cm-btn cm-btn-secondary cm-btn-sm"
            @click="previewBalance"
            :disabled="rebalancing || balancing || !actionsEnabled"
          >
            <FontAwesomeIcon :icon="['fas', 'scale-balanced']" style="font-size: 11px;" />
            {{ balancing ? 'Balancing…' : (scopeMode === 'my_team' ? 'My Team Rebalance' : 'Rebalance Opportunities') }}
          </button>
        </div>
      </div>
      <Worklist_Assignments
        v-if="metricsLoaded"
        :metric-map="metricMap"
        :member-entity-types="['CM Manager', 'CM Staff']"
        member-label="Care Manager"
        stage-label="Stage"
        :task-type-map="cmTaskTypeMap"
        height="auto"
      />
    </div>

    <Teleport to="body">
      <div v-if="rebalanceResult" class="cm-overlay" @click.self="rebalanceResult = null">
        <div class="cm-modal">
          <div class="cm-modal-head">
            <h3>Assignment Result</h3>
            <button class="cm-icon-btn" @click="rebalanceResult = null"><FontAwesomeIcon :icon="['fas', 'xmark']" /></button>
          </div>
          <div class="cm-modal-body">
            <template v-if="rebalanceResult.error">
              <div style="font-size: 12.5px; color: #D7402B;">{{ rebalanceResult.error }}</div>
            </template>
            <template v-else>
              <div style="font-size: 26px; font-weight: 700; color: #353537;">{{ rebalanceResult.tasks_moved }} tasks assigned</div>
              <div v-if="rebalanceResult.tasks_no_candidates > 0" style="font-size: 12.5px; color: #B45309;">
                {{ rebalanceResult.tasks_no_candidates }} tasks could not be assigned (no matching rules)
              </div>
              <div v-if="rebalanceResult.unassignable_reasons && rebalanceResult.unassignable_reasons.length" style="display: flex; flex-direction: column; gap: 6px; margin-top: 8px;">
                <div v-for="(r, i) in rebalanceResult.unassignable_reasons" :key="i"
                  style="padding: 6px 8px; border-radius: 6px; font-size: 11.5px; background: #FDF4E7;">
                  <span style="font-weight: 500; color: #353537;">{{ r.program }}</span>
                  <span style="color: #A5A5A6;"> · </span>
                  <span style="color: #626264;">{{ r.task_type }}</span>
                  <span style="color: #A5A5A6;"> · </span>
                  <span style="font-weight: 600; color: #92400E;">{{ r.count }} tasks</span>
                  <div style="font-size: 10px; margin-top: 2px; color: #92400E;">{{ r.reason }}</div>
                </div>
              </div>
              <div v-if="rebalanceResult.tasks_moved === 0 && !rebalanceResult.tasks_no_candidates" style="font-size: 12.5px; color: #A5A5A6;">
                No unassigned tasks found that match existing rules.
              </div>
            </template>
          </div>
          <div class="cm-modal-foot">
            <button class="cm-btn cm-btn-primary" @click="rebalanceResult = null">Done</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Rebalance Preview / Confirm Modal -->
    <Teleport to="body">
      <div v-if="balancePreview" class="cm-overlay" @click.self="closeBalance">
        <div class="cm-modal" style="max-width: 560px;">
          <div class="cm-modal-head">
            <h3>{{ balanceApplied ? 'Rebalance Complete' : (previewIsMyTeam ? 'My Team Rebalance Preview' : 'Rebalance Preview') }}</h3>
            <button class="cm-icon-btn" @click="closeBalance"><FontAwesomeIcon :icon="['fas', 'xmark']" /></button>
          </div>
          <div class="cm-modal-body">
            <template v-if="balancePreview.error">
              <div style="font-size: 12.5px; color: #D7402B;">{{ balancePreview.error }}</div>
            </template>
            <template v-else>
              <template v-if="previewIsMyTeam && !balanceApplied">
                <div style="font-size: 26px; font-weight: 700; color: #353537;">
                  {{ balancePreview.patients_moved }} {{ 'patient' + (balancePreview.patients_moved !== 1 ? 's' : '') }} would move
                </div>
                <div style="font-size: 12px; color: #626264; margin-top: 2px;">
                  {{ balancePreview.patients_in_scope }} patients in scope ·
                  {{ balancePreview.tasks_moved }} tasks ·
                  {{ balancePreview.cases_moved }} cases
                </div>
                <div v-if="balancePreview.patients_no_candidates > 0" style="font-size: 12px; color: #B45309; margin-top: 6px;">
                  {{ balancePreview.patients_no_candidates }} patients can't be rebalanced (no matching rule or single owner).
                </div>
              </template>
              <template v-else-if="previewIsMyTeam && balanceApplied">
                <div style="font-size: 26px; font-weight: 700; color: #353537;">
                  {{ balancePreview.patients_moved }} {{ 'patient' + (balancePreview.patients_moved !== 1 ? 's' : '') }} moved
                </div>
                <div style="font-size: 12px; color: #626264; margin-top: 2px;">
                  {{ balancePreview.tasks_moved }} tasks · {{ balancePreview.cases_moved }} cases
                </div>
              </template>
              <template v-else>
                <div style="font-size: 26px; font-weight: 700; color: #353537;">
                  {{ balancePreview.tasks_moved }} {{ balanceApplied ? 'opportunities moved' : 'opportunities would move' }}
                </div>
                <div style="font-size: 12px; color: #626264; margin-top: 2px;">
                  Only opportunities whose assignment rules allow more than one care manager can move —
                  single-owner practices and contract-scoped cases stay put.
                </div>
                <div v-if="balancePreview.tasks_no_candidates > 0" style="font-size: 12px; color: #B45309; margin-top: 6px;">
                  {{ balancePreview.tasks_no_candidates }} opportunities can't be rebalanced (single owner or no matching rule).
                </div>
              </template>

              <div v-if="balanceRows.length" style="margin-top: 14px; max-height: 300px; overflow-y: auto;">
                <table style="width: 100%; table-layout: fixed; font-size: 12.5px;">
                  <colgroup><col style="width: 60%;" /><col style="width: 20%;" /><col style="width: 20%;" /></colgroup>
                  <thead>
                    <tr style="font-size: 11px; color: #A5A5A6; border-bottom: 1px solid #EEE;">
                      <th style="text-align: left; padding: 4px 0;">Care Manager</th>
                      <th style="text-align: right;">Before</th><th style="text-align: right;">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in balanceRows" :key="r.name">
                      <td class="sensitive" style="color: #353537; padding: 4px 0;">{{ r.name }}</td>
                      <td style="text-align: right; color: #A5A5A6;">{{ r.before }}</td>
                      <td style="text-align: right; font-weight: 600;">{{ r.after }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else style="font-size: 12.5px; color: #A5A5A6; margin-top: 10px;">
                Nothing to rebalance — every eligible opportunity is already at the balanced owner.
              </div>
            </template>
          </div>
          <div class="cm-modal-foot" style="display: flex; gap: 8px; justify-content: flex-end;">
            <template v-if="balanceApplied || balancePreview.error">
              <button class="cm-btn cm-btn-primary" @click="closeBalance">Done</button>
            </template>
            <template v-else>
              <button class="cm-btn cm-btn-secondary" @click="closeBalance">Cancel</button>
              <button
                class="cm-btn cm-btn-primary"
                :disabled="balancing || (previewIsMyTeam ? balancePreview.patients_moved === 0 || !pendingPreviewToken : balancePreview.tasks_moved === 0)"
                @click="applyBalance"
              >
                {{ balancing ? 'Applying…' : 'Apply Rebalance' }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { mapGetters } from 'vuex';
import { z } from 'zod';
import abortable from '@/mixins/abortable';
import Worklist_Assignments from '@/components/widgetcomponents/Worklist_Assignments.vue';
import { listCareManagementPrograms } from '@/services/api/careManagementPrograms';

const myTeamSchema = z.object({
  available: z.boolean().default(false),
  member_ids: z.array(z.number()).default([]),
  members: z.array(z.object({ id: z.number(), name: z.string() })).default([]),
});

function myTeamStoreKey(tenantSlug, userId) {
  if (!tenantSlug || !userId) return null;
  return `my_team_map:${tenantSlug}:${userId}`;
}

export default {
  name: 'TeamAssignments',
  components: { FontAwesomeIcon, Worklist_Assignments },
  mixins: [abortable],
  data() {
    return {
      metricMap: {},
      metricIdToName: {},
      metricsLoaded: false,
      cmTaskTypeMap: [],
      rebalancing: false,
      rebalanceResult: null,
      ruleCount: null,
      balancing: false,
      balancePreview: null,
      balanceApplied: false,
      previewIsMyTeam: false,
      pendingPreviewToken: null,
      pendingPreviewMetricIds: null,
      pendingPreviewUnassignedOnly: false,
      previewContext: null,
      scopeMode: 'all',
      myTeam: { available: false, member_ids: [], members: [] },
      myTeamLoading: false,
    };
  },
  computed: {
    ...mapGetters('tenant', ['current_tenant_slug']),
    ...mapGetters('auth', ['current_user']),
    currentUserId() { return this.current_user?.id ?? null; },
    myTeamMappingReady() {
      return this.myTeam.available && this.myTeam.member_ids.length > 0 && !this.myTeamLoading;
    },
    actionsEnabled() {
      if (!this.metricsLoaded || Object.keys(this.metricMap).length === 0) return false;
      if (this.scopeMode === 'my_team' && !this.myTeamMappingReady) return false;
      return true;
    },
    metricIds() { return Object.values(this.metricMap); },
    balanceRows() {
      const p = this.balancePreview;
      if (!p || p.error) return [];
      const before = p.caseloads_before || {};
      const after = p.caseloads_after || {};
      const names = new Set([...Object.keys(before), ...Object.keys(after)]);
      return [...names]
        .map(name => ({ name, before: before[name] || 0, after: after[name] || 0 }))
        .sort((a, b) => b.after - a.after || a.name.localeCompare(b.name));
    },
  },
  watch: {
    current_tenant_slug(newSlug, oldSlug) {
      if (newSlug !== oldSlug) this._reloadForContext();
    },
    currentUserId(newId, oldId) {
      if (newId !== oldId) this._reloadForContext();
    },
  },
  methods: {
    _reloadForContext() {
      this.closeBalance();
      this.rebalanceResult = null;
      this._renewAbortSignal('balance');
      this._renewAbortSignal('assign');
      this.rebalancing = false;
      this.myTeam = { available: false, member_ids: [], members: [] };
      this.scopeMode = 'all';
      this.metricMap = {};
      this.metricIdToName = {};
      this.metricsLoaded = false;
      this.cmTaskTypeMap = [];
      this.ruleCount = null;
      this.fetchMyTeam();
      this.fetchMetrics().then(() => this.fetchTaskTypeMap());
    },
    async fetchMyTeam() {
      const signal = this._renewAbortSignal('team');
      const tenantSlug = this.current_tenant_slug;
      const userId = this.currentUserId;
      const key = myTeamStoreKey(tenantSlug, userId);
      if (!key) { this.myTeam = { available: false, member_ids: [], members: [] }; return; }
      this.myTeamLoading = true;
      try {
        const { data } = await axios.get('/api/team-assignments/my-team/', { signal });
        if (signal.aborted) return;
        if (myTeamStoreKey(this.current_tenant_slug, this.currentUserId) !== key) return;
        const parsed = myTeamSchema.parse(data);
        this.myTeam = { available: parsed.available, member_ids: parsed.member_ids, members: parsed.members };
      } catch (e) {
        if (this._isAborted(e)) return;
        if (myTeamStoreKey(this.current_tenant_slug, this.currentUserId) !== key) return;
        this.myTeam = { available: false, member_ids: [], members: [] };
      } finally {
        if (!signal?.aborted) this.myTeamLoading = false;
      }
    },
    async fetchMetrics() {
      const signal = this._renewAbortSignal('metrics');
      const context = myTeamStoreKey(this.current_tenant_slug, this.currentUserId);
      try {
        const metrics = await listCareManagementPrograms({ signal });
        if (signal.aborted || context !== myTeamStoreKey(this.current_tenant_slug, this.currentUserId)) return;
        const map = {};
        const idToName = {};
        for (const m of metrics) {
          const name = m.display_name || m.name;
          map[name] = m.id;
          idToName[m.id] = name;
        }
        this.metricMap = map;
        this.metricIdToName = idToName;
      } catch (e) {
        if (this._isAborted(e) || context !== myTeamStoreKey(this.current_tenant_slug, this.currentUserId)) return;
        console.error('Error fetching care management metrics:', e);
      } finally {
        if (!signal.aborted) this.metricsLoaded = true;
      }
    },
    async fetchTaskTypeMap() {
      const signal = this._renewAbortSignal('stages');
      const context = myTeamStoreKey(this.current_tenant_slug, this.currentUserId);
      const metricIds = Object.values(this.metricMap).join(',');
      if (!metricIds) { this.cmTaskTypeMap = []; this.ruleCount = 0; return; }
      try {
        const { data } = await axios.get('/api/metrictasktypes/', { params: { metric_ids: metricIds }, signal });
        if (signal.aborted || context !== myTeamStoreKey(this.current_tenant_slug, this.currentUserId)) return;
        this.cmTaskTypeMap = data.map(mtt => ({
          program: this.metricIdToName[mtt.metric] || `Metric ${mtt.metric}`,
          tier: mtt.task_type_details?.name || `Task Type ${mtt.task_type}`,
          id: mtt.task_type,
        }));
        this.ruleCount = new Set(this.cmTaskTypeMap.map(t => t.program)).size;
      } catch (e) { if (!this._isAborted(e)) console.error('Error fetching task type map:', e); }
    },
    async runRebalance() {
      if (!this.actionsEnabled || this.rebalancing) return;
      if (this.scopeMode === 'my_team') { this.previewBalance({ unassignedOnly: true }); return; }
      const signal = this._renewAbortSignal('assign');
      const context = myTeamStoreKey(this.current_tenant_slug, this.currentUserId);
      this.rebalancing = true;
      try {
        const { data } = await axios.post('/api/team-assignments/rebalance/', {
          dry_run: false, unassigned_only: true,
          metric_ids: this.metricIds,
        }, { signal });
        if (signal.aborted || context !== myTeamStoreKey(this.current_tenant_slug, this.currentUserId)) return;
        this.rebalanceResult = data;
      } catch (e) {
        if (signal.aborted || context !== myTeamStoreKey(this.current_tenant_slug, this.currentUserId)) return;
        this.rebalanceResult = { error: e.response?.data?.error || e.message || 'Assignment failed' };
      } finally { if (!signal.aborted) this.rebalancing = false; }
    },
    async previewBalance({ unassignedOnly = false } = {}) {
      if (!this.actionsEnabled || this.balancing) return;
      const signal = this._renewAbortSignal('balance');
      const context = myTeamStoreKey(this.current_tenant_slug, this.currentUserId);
      this.balancing = true;
      this.balanceApplied = false;
      this.pendingPreviewToken = null;
      this.pendingPreviewMetricIds = null;
      const scopeSnapshot = this.scopeMode;
      const metricIdsSnapshot = [...this.metricIds];
      try {
        const payload = { dry_run: true, metric_ids: metricIdsSnapshot };
        if (scopeSnapshot === 'my_team') payload.my_team = true;
        if (unassignedOnly) payload.unassigned_only = true;
        const { data } = await axios.post('/api/team-assignments/rebalance/', payload, { signal });
        if (signal.aborted || context !== myTeamStoreKey(this.current_tenant_slug, this.currentUserId) || scopeSnapshot !== this.scopeMode) return;
        this.previewContext = context;
        this.pendingPreviewMetricIds = metricIdsSnapshot;
        this.previewIsMyTeam = scopeSnapshot === 'my_team';
        this.pendingPreviewUnassignedOnly = unassignedOnly;
        this.balancePreview = data;
        if (scopeSnapshot === 'my_team' && data.preview_token) {
          this.pendingPreviewToken = data.preview_token;
          this.pendingPreviewMetricIds = metricIdsSnapshot;
        }
      } catch (e) {
        if (signal.aborted || context !== myTeamStoreKey(this.current_tenant_slug, this.currentUserId)) return;
        this.previewIsMyTeam = scopeSnapshot === 'my_team';
        this.balancePreview = { error: e.response?.data?.error || e.message || 'Preview failed' };
      } finally { if (!signal.aborted) this.balancing = false; }
    },
    async applyBalance() {
      if (!this.actionsEnabled || this.balancing || !this.balancePreview || this.balancePreview.error) return;
      const context = myTeamStoreKey(this.current_tenant_slug, this.currentUserId);
      if (context !== this.previewContext || this.previewIsMyTeam !== (this.scopeMode === 'my_team')) {
        this.closeBalance();
        return;
      }
      if (this.previewIsMyTeam && !this.pendingPreviewToken) {
        this.balancePreview = { error: 'Preview My Team again before applying.' };
        return;
      }
      const signal = this._renewAbortSignal('balance');
      this.balancing = true;
      try {
        const payload = { dry_run: false, metric_ids: this.pendingPreviewMetricIds };
        if (this.pendingPreviewUnassignedOnly) payload.unassigned_only = true;
        if (this.previewIsMyTeam) {
          payload.my_team = true;
          payload.preview_token = this.pendingPreviewToken;
        }
        const { data } = await axios.post('/api/team-assignments/rebalance/', payload, { signal });
        if (signal.aborted || context !== myTeamStoreKey(this.current_tenant_slug, this.currentUserId)) return;
        this.balancePreview = data;
        this.balanceApplied = true;
        this.pendingPreviewToken = null;
        this.pendingPreviewMetricIds = null;
      } catch (e) {
        if (signal.aborted || context !== myTeamStoreKey(this.current_tenant_slug, this.currentUserId)) return;
        this.balancePreview = { error: e.response?.data?.error || e.response?.data?.detail || e.message || 'Rebalance failed' };
      } finally { if (!signal.aborted) this.balancing = false; }
    },
    closeBalance() {
      this._renewAbortSignal('balance');
      this.balancing = false;
      this.previewContext = null;
      this.balancePreview = null;
      this.balanceApplied = false;
      this.previewIsMyTeam = false;
      this.pendingPreviewToken = null;
      this.pendingPreviewMetricIds = null;
    },
  },
  async mounted() {
    await Promise.all([this.fetchMyTeam(), this.fetchMetrics()]);
    await this.fetchTaskTypeMap();
  },
};
</script>
